/**
 * The email CREATION card's own small rules (busy-my-web#650, Figma section 40008595:224771
 * "Email creation mode"). Pure — no Vue, no store — so each rule is testable on its own and the
 * components only draw what these answer.
 *
 * Frames referred to below are the section's journey frames by name: "default", "recipient added",
 * "add some recipients", "recipients collapsed", "waiting on 1–4", "click improve by AI",
 * "AI suggested", "write a prompt", "schedule send 1–5", "click on close or everywhere".
 */
import { scheduleClock, scheduleDay } from '@/lib/scheduledSend'

/** The creation card's header — "Briefing new email" (frame "default"), lower-case as drawn. */
export const NEW_EMAIL_TITLE = 'Briefing new email'

/**
 * The recipient row's label. "To:" while the row says one thing (the placeholder, the caret, or a
 * single address — frames "default", "click to add recipient", "recipient added"); "To" once it
 * holds two or more, where the addresses themselves follow as pills or a comma list (frames "add
 * some recipients", "recipients collapsed").
 *
 * @param {number} total  how many recipients (To + Cc + Bcc) the email has
 */
export function recipientToLabel(total) {
  return Number(total) > 1 ? 'To' : 'To:'
}

const toMoment = (value) => {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return isNaN(d.getTime()) ? null : d
}

/**
 * A waiting-on date picked with "No time" (frame "waiting on 2") is kept at the day's local
 * midnight, so midnight IS the date-only answer — nobody waits on an email until 12:00 AM.
 */
export function isDateOnly(value) {
  const d = toMoment(value)
  return !!d && d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0
}

/**
 * The waiting-on fact in the Briefing: "Oct 21" for a day alone (frame "waiting on 2"), "Jul 22, at
 * 12:30 PM" once a time is chosen (frame "waiting on 4") — the scheduled-send fact's own spelling
 * without its "Send" verb, so the two clocks in the strip read alike. '' when there is no date.
 */
export function waitingFactText(value) {
  const day = scheduleDay(value)
  if (!day) return ''
  if (isDateOnly(value)) return day
  return `${day}, at ${scheduleClock(value)}`
}

/**
 * The popover header's chip: "16 Oct 26" (a day alone) or "21 Oct 26 at 3:00 pm" (frames "waiting
 * on 1–3", "schedule send 2–3"). '' when there is nothing to show.
 *
 * @param value     the picked (or offered) moment
 * @param dateOnly  true when the pick carries no time
 */
export function popoverChipText(value, dateOnly = false) {
  const d = toMoment(value)
  if (!d) return ''
  const day = `${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'short' })} ${String(d.getFullYear()).slice(-2)}`
  if (dateOnly) return day
  return `${day} at ${scheduleClock(d).toLowerCase()}`
}

/**
 * Should a picker preview be written into the email as the user picks (frames "waiting on 2–3" and
 * "schedule send 3": the Briefing fills while the popover is still open)? Only a REAL pick — the
 * picker also previews the day it merely offers on opening ("16 Oct 26", frame "waiting on 1"),
 * which nobody chose.
 */
export function previewIsPick(preview) {
  return !!(preview && preview.start && preview.picked !== false)
}

// ── Improve with Busy AI — the "Suggested" panel (frames "click improve by AI" → "use this") ─────

/** The five tones, left to right, Professional first and chosen on open (frame "click improve by AI"). */
export const AI_TONES = Object.freeze(['Professional', 'Friendly', 'Formal', 'Casual', 'Concise'])

/** The four quick refinements offered over the prompt while it is being written (frame "write a prompt a"). */
export const AI_REFINES = Object.freeze(['Shorter', 'Longer', 'Add greeting', 'Fix grammar'])

/**
 * The steering line the `ai/improve` endpoint is given. The endpoint takes one free-text `hint`, so
 * the tone, a quick refinement and the typed prompt travel as one sentence each — no backend change.
 *
 * @param {{ tone?: string, refine?: string, prompt?: string }} ask
 */
export function improveHint({ tone = '', refine = '', prompt = '' } = {}) {
  const parts = []
  if (String(tone).trim()) parts.push(`Use a ${String(tone).trim().toLowerCase()} tone.`)
  if (String(refine).trim()) parts.push(`${String(refine).trim()}.`)
  if (String(prompt).trim()) parts.push(String(prompt).trim())
  return parts.join(' ')
}

/**
 * The Suggested panel's state, as the frames draw it: 'loading' (skeleton, "Thinking..." in the
 * prompt — frames "click improve by AI", "write a prompt c"), 'ready' (the suggestion — "AI
 * suggested"), or 'idle' (nothing asked yet).
 */
export function suggestState({ busy = false, suggestion = null } = {}) {
  if (busy) return 'loading'
  return suggestion && String(suggestion.text || '').trim() ? 'ready' : 'idle'
}

// ── Leaving the card (frame "click on close or everywhere") ─────────────────────────────────────

/** The notice Close leaves behind once a composed email has been kept as a Gmail draft. */
export const DRAFT_SAVED_TEXT = 'Message saved as draft'

/** Where its "View" goes: the mailbox's Drafts folder. */
export const DRAFTS_PATH = '/emails/draft'

/**
 * Does closing this composer keep a draft worth announcing? A fresh email (not a reply — that one
 * threads elsewhere) that has anything in it at all.
 *
 * @param {{ mode?: string, replying?: boolean, hasContent?: boolean }} state
 */
export function closeKeepsDraft({ mode = null, replying = false, hasContent = false } = {}) {
  return mode === 'emails' && !replying && !!hasContent
}

export default {
  NEW_EMAIL_TITLE,
  recipientToLabel,
  isDateOnly,
  waitingFactText,
  popoverChipText,
  previewIsPick,
  AI_TONES,
  AI_REFINES,
  improveHint,
  suggestState,
  DRAFT_SAVED_TEXT,
  DRAFTS_PATH,
  closeKeepsDraft,
}
